import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { AttachmentMetaDataModel, NewsAttachmentUploadModel, NewsItemModel } from '../../../../api-service/clients';
import { toStartOfDay } from '../../../../utilities/date-helper';
import { blobToBase64 } from '../../../../hooks/audio/blob-to-base64';
import { CreateUpdateNewsDialogComponent, INewsForm, ISavedNewsFlags } from './create-update-news-dialog-component';
import { useNewsItemAttachmentActions, useNewsItemAttachmentDownload } from './download-news-item-attachment';

const client = moduleClientFactory.createForumCaseworkerClient();

export interface ICreateUpdateNewsDialogProps {
  newsItemToUpdate?: NewsItemModel;
  canShowToAll: boolean;
  canAttachFiles: boolean;
  onSaved: () => void;
  onCancel: () => void;
}

export function CreateUpdateNewsDialog(props: ICreateUpdateNewsDialogProps) {
  const { newsItemToUpdate, canShowToAll, canAttachFiles, onSaved, onCancel } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [savedFlags, setSavedFlags] = useState<ISavedNewsFlags>();
  const downloadAttachment = useNewsItemAttachmentDownload();
  const attachmentActions = useNewsItemAttachmentActions();
  const newsItemId = newsItemToUpdate?.id;

  useEffect(() => {
    setSavedFlags(undefined);
    if (!newsItemId) {
      return;
    }
    client
      .getNewsItem({ newsItemId })
      .then(result => setSavedFlags({ journalize: result.journalize, enableShowInterest: result.enableShowInterest }))
      .catch(() => {
        setSavedFlags(undefined);
        enqueueSnackbar('Kan ikke hente nyhedens indstillinger.', { variant: 'warning' });
      });
  }, [newsItemId]);

  async function save(form: INewsForm) {
    if (newsItemToUpdate && !newsItemToUpdate.canDelete) {
      return;
    }
    setIsSaving(true);
    try {
      const request = {
        title: form.title.trim(),
        content: form.content.trim(),
        url: form.url.trim(),
        showFromDate: toStartOfDay(form.showFromDate) as Date,
        expirationDate: toStartOfDay(form.expirationDate),
        attachments: await Promise.all(form.attachments.map(toAttachmentUpload)),
      };

      if (newsItemToUpdate) {
        // Pantheon drops every attachment an update does not name.
        const existingAttachmentIds = form.existingAttachments.map(attachment => attachment.id);
        await client.updateNewsItem({ newsItemId: newsItemToUpdate.id, existingAttachmentIds, ...request });
        enqueueSnackbar('Nyheden er opdateret.', { variant: 'success' });
      } else {
        await client.createNewsItem({ ...request, showToAll: form.showToAll, journalize: form.journalize, enableShowInterest: form.enableShowInterest });
        enqueueSnackbar('Nyheden er oprettet.', { variant: 'success' });
      }
      onSaved();
    } catch {
      enqueueSnackbar(newsItemToUpdate ? 'Kan ikke opdatere nyheden' : 'Kan ikke oprette nyheden', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }

  const existingAttachmentActions =
    attachmentActions && newsItemId
      ? {
          isFetching: attachmentActions.isFetching,
          onOpen: (attachment: AttachmentMetaDataModel) => attachmentActions.onOpen(newsItemId, attachment),
          onDownload: (attachment: AttachmentMetaDataModel) => attachmentActions.onDownload(newsItemId, attachment),
        }
      : undefined;

  async function openAttachment(attachment: AttachmentMetaDataModel) {
    if (!newsItemId) {
      return;
    }
    await downloadAttachment(newsItemId, attachment);
  }

  return (
    <CreateUpdateNewsDialogComponent
      newsItemToUpdate={newsItemToUpdate}
      canShowToAll={canShowToAll}
      canAttachFiles={canAttachFiles}
      isSaving={isSaving}
      savedFlags={savedFlags}
      onSave={save}
      onCancel={onCancel}
      onOpenAttachment={openAttachment}
      attachmentActions={existingAttachmentActions}
    />
  );
}

async function toAttachmentUpload(file: File): Promise<NewsAttachmentUploadModel> {
  return { content: await blobToBase64(file), fileName: file.name, mimeType: file.type };
}
